import React from "react";
import { ProjectsStore } from "src/modules/common/stores";
import { TasksGridVisualStore, TasksFiltersVisualStore } from "../stores";
import { observer } from "mobx-react-lite";
import { FilterOutlined } from "@ant-design/icons";
import { Button, Checkbox, Dropdown, Tooltip } from "antd";
import { ColumnKeys } from "./TaskList";
import MetadataFilterControls from "./MetadataFilterControls";
import { ExtraDateFilter } from "src/modules/administration/types/DashboardWidgetMeta";

type Props = {
    tasksGridStore: TasksGridVisualStore;
    filterStore: TasksFiltersVisualStore;
    projectsStore: ProjectsStore;
};

export const METADATA_CHANGES_FILTER_KEY = "METADATA_CHANGES";

const FiltersControls: React.FC<Props> = ({ filterStore, tasksGridStore }) => {
    // Helper function to check if a filter is disabled due to widget filter
    const isFilterDisabled = (entityReference: string): boolean => {
        const filter = filterStore.columnsFilterDict[entityReference];
        return filter?.isWidgetFilter === true;
    };

    const getStatusMenu = () => {
        return filterStore.taskStatusesForCurrentProject.map((t) => {
            const taskType = filterStore.taskTypesForCurrentProject.find(
                (type) => type.id === t.typeId,
            )!.name;
            return {
                key: t.id,
                label: (
                    <div
                        onClick={(e) => {
                            filterStore.setCurrentFilterValue(
                                ColumnKeys.Status,
                                t.id,
                                false,
                            );
                            e.stopPropagation(); // Prevent the menu from closing
                        }}
                        data-testid={`task-status-${t.id}-type-${taskType}`}
                    >
                        <Checkbox
                            style={{ marginRight: 10 }}
                            checked={filterStore.columnsFilterDict[
                                ColumnKeys.Status
                            ]?.value.includes(t.id)}
                        />
                        {`${t.name} (${filterStore.taskTypesForCurrentProject.find((type) => type.id === t.typeId)!.name})`}
                    </div>
                ),
            };
        });
    };

    const getTypeMenu = () => {
        return filterStore.taskTypesForCurrentProject.map((t) => ({
            key: t.id,
            label: (
                <div
                    onClick={(e) => {
                        filterStore.setCurrentFilterValue(
                            ColumnKeys.Type,
                            t.id,
                            false,
                        );
                        e.stopPropagation(); // Prevent the menu from closing
                    }}
                    data-testid={`task-type-${t.id}`}
                >
                    <Checkbox
                        style={{ marginRight: 10 }}
                        checked={filterStore.columnsFilterDict[
                            ColumnKeys.Type
                        ]?.value.includes(t.id)}
                    />
                    {t.name}
                </div>
            ),
        }));
    };

    const getPrioritiesMenu = () => {
        const priorities = [
            { key: "LOW", label: "Low" },
            { key: "MEDIUM", label: "Medium" },
            { key: "HIGH", label: "High" },
        ];

        return priorities.map((t) => ({
            key: t.key,
            label: (
                <div
                    onClick={(e) => {
                        filterStore.setCurrentFilterValue(
                            ColumnKeys.Priority,
                            t.key,
                            false,
                        );
                        e.stopPropagation(); // Prevent the menu from closing
                    }}
                    data-testid={`task-priority-${t.key}`}
                >
                    <Checkbox
                        style={{ marginRight: 10 }}
                        checked={filterStore.columnsFilterDict[
                            ColumnKeys.Priority
                        ]?.value.includes(t.key)}
                    />
                    {t.label}
                </div>
            ),
        }));
    };

    const getUsersMenu = (columnName: string) => {
        return Object.entries(filterStore.allUsersFullNameResolver)
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map((u) => ({
                key: `${columnName}${u[0]}`,
                label: (
                    <div
                        onClick={(e) => {
                            filterStore.setCurrentFilterValue(
                                columnName,
                                u[0],
                                false,
                            );
                            e.stopPropagation(); // Prevent the menu from closing
                        }}
                        data-testid={`task-${columnName}-${u[0]}`}
                    >
                        <Checkbox
                            style={{ marginRight: 10 }}
                            checked={filterStore.columnsFilterDict[
                                columnName
                            ]?.value.includes(u[0])}
                        />
                        {u[1]}
                    </div>
                ),
            }));
    };

    const getAttachmentsMenu = () => {
        const items = [
            {
                key: "has",
                label: "has",
            },
            {
                key: "hasNo",
                label: "has no",
            },
        ];
        return items.map((item) => ({
            key: item.key,
            label: (
                <div
                    onClick={() => {
                        filterStore.setCurrentFilterValue(
                            ColumnKeys.Attachments,
                            item.key === "has" ? 1 : 0,
                            false,
                        );
                    }}
                >
                    {item.label}
                </div>
            ),
        }));
    };

    const getCustomMenu = (columnKey: string) => {
        const items = [
            {
                key: true,
                label: "is",
            },
            {
                key: false,
                label: "is not",
            },
        ];
        return items.map((item) => ({
            key: `${columnKey}${item.key}`,
            label: (
                <div
                    onClick={() => {
                        filterStore.setCurrentFilterValue(
                            columnKey,
                            item.key,
                            false,
                        );
                    }}
                >
                    {item.label}
                </div>
            ),
        }));
    };

    const getMetadataChangesMenu = () => {
        const items = [
            {
                key: "with_changes",
                label: "Only with changes",
            },
            {
                key: "without_changes",
                label: "Only without changes",
            },
        ];
        return items.map((item) => ({
            key: item.key,
            label: (
                <div
                    onClick={() => {
                        filterStore.setCurrentFilterValue(
                            METADATA_CHANGES_FILTER_KEY,
                            item.key === "with_changes" ? 1 : 0,
                            false,
                        );
                    }}
                >
                    {item.label}
                </div>
            ),
        }));
    };

    const handleCustomDateClick = (columnName: string, isMetadata: boolean) => {
        filterStore.addCustomDateFilter(columnName, isMetadata);
    };

    const getDateMenu = (columnName: string, isMetadata: boolean) => {
        const handleDateClick = (days: number) => {
            const extraDateFilter = {
                periodValue: "D",
                periodCount: days,
            } as ExtraDateFilter;
            filterStore.setDateFilterValue(
                columnName,
                extraDateFilter,
                isMetadata,
            );
        };

        const dates =
            columnName === ColumnKeys.DateCreated || isMetadata
                ? [
                      {
                          key: `${columnName}dayAgo`,
                          label: (
                              <div onClick={() => handleDateClick(1)}>
                                  1 day
                              </div>
                          ),
                      },
                      {
                          key: `${columnName}threeDaysAgo`,
                          label: (
                              <div onClick={() => handleDateClick(3)}>
                                  3 days
                              </div>
                          ),
                      },
                      {
                          key: `${columnName}weekAgo`,
                          label: (
                              <div onClick={() => handleDateClick(7)}>
                                  1 week
                              </div>
                          ),
                      },
                      {
                          key: `${columnName}monthAgo`,
                          label: (
                              <div onClick={() => handleDateClick(30)}>
                                  1 month
                              </div>
                          ),
                      },
                      {
                          key: `${columnName}customDates`,
                          label: (
                              <div
                                  onClick={() =>
                                      handleCustomDateClick(
                                          columnName,
                                          isMetadata,
                                      )
                                  }
                              >
                                  Custom dates...
                              </div>
                          ),
                      },
                  ]
                : [
                      {
                          key: `${columnName}overDue`,
                          label: (
                              <div onClick={() => handleDateClick(0)}>
                                  overdue
                              </div>
                          ),
                      },
                      {
                          key: `${columnName}oneDay`,
                          label: (
                              <div onClick={() => handleDateClick(1)}>
                                  1 day
                              </div>
                          ),
                      },
                      {
                          key: `${columnName}threeDays`,
                          label: (
                              <div onClick={() => handleDateClick(3)}>
                                  3 days
                              </div>
                          ),
                      },
                      {
                          key: `${columnName}oneWeek`,
                          label: (
                              <div onClick={() => handleDateClick(7)}>
                                  1 week
                              </div>
                          ),
                      },
                      {
                          key: `${columnName}oneMonth`,
                          label: (
                              <div onClick={() => handleDateClick(30)}>
                                  1 month
                              </div>
                          ),
                      },
                      {
                          key: `${columnName}noDueDate`,
                          label: (
                              <div
                                  onClick={() =>
                                      filterStore.setDateFilterValue(
                                          columnName,
                                          null,
                                          false,
                                      )
                                  }
                              >
                                  no due date
                              </div>
                          ),
                      },
                      {
                          key: `${columnName}customDates`,
                          label: (
                              <div
                                  onClick={() =>
                                      handleCustomDateClick(
                                          columnName,
                                          isMetadata,
                                      )
                                  }
                              >
                                  Custom dates...
                              </div>
                          ),
                      },
                  ];
        return dates;
    };

    const generalMenuItems = [
        {
            key: "general",
            type: "group", // Group for general menu items
            label: (
                <span className="filter-menu-group-title">General filters</span>
            ),
            children: [
                {
                    key: ColumnKeys.Status,
                    label: "Status",
                    children: getStatusMenu(),
                    popupClassName: "task-filters-sub-menu ",
                    disabled: isFilterDisabled(ColumnKeys.Status),
                },
                {
                    key: ColumnKeys.Type,
                    label: "Type",
                    children: getTypeMenu(),
                    popupClassName: "task-filters-sub-menu ",
                    disabled: isFilterDisabled(ColumnKeys.Type),
                },
                {
                    key: ColumnKeys.Priority,
                    label: "Priority",
                    children: getPrioritiesMenu(),
                    disabled: isFilterDisabled(ColumnKeys.Priority),
                },
                {
                    key: ColumnKeys.Owner,
                    label: "Owner",
                    children: getUsersMenu(ColumnKeys.Owner),
                    popupClassName: "task-filters-sub-menu ",
                    disabled: isFilterDisabled(ColumnKeys.Owner),
                },
                {
                    key: ColumnKeys.AssignedTo,
                    label: "Assignee",
                    children: getUsersMenu(ColumnKeys.AssignedTo),
                    popupClassName: "task-filters-sub-menu ",
                    disabled: isFilterDisabled(ColumnKeys.AssignedTo),
                },
                {
                    key: ColumnKeys.Attachments,
                    label: "Attachments",
                    children: getAttachmentsMenu(),
                    disabled: isFilterDisabled(ColumnKeys.Attachments),
                },
                {
                    key: ColumnKeys.DateCreated,
                    label: "Created date",
                    children: getDateMenu(ColumnKeys.DateCreated, false),
                    disabled: isFilterDisabled(ColumnKeys.DateCreated),
                },
                {
                    key: ColumnKeys.DueDate,
                    label: "Due date",
                    children: getDateMenu(ColumnKeys.DueDate, false),
                    disabled: isFilterDisabled(ColumnKeys.DueDate),
                },
                {
                    key: ColumnKeys.SubTasks,
                    label: "Subtasks",
                    children: getCustomMenu(ColumnKeys.SubTasks),
                    disabled: isFilterDisabled(ColumnKeys.SubTasks),
                },
                {
                    key: ColumnKeys.DoneTasks,
                    label: "Completed tasks",
                    children: getCustomMenu(ColumnKeys.DoneTasks),
                    disabled: isFilterDisabled(ColumnKeys.DoneTasks),
                },
                {
                    key: METADATA_CHANGES_FILTER_KEY,
                    label: "Metadata changes",
                    children: getMetadataChangesMenu(),
                    disabled: isFilterDisabled(METADATA_CHANGES_FILTER_KEY),
                },
            ],
        },
    ];

    const metadataMenuItems =
        tasksGridStore.tableMetaData.length > 0
            ? [
                  {
                      key: "metadata",
                      type: "group", // Group for metadata menu items
                      label: (
                          <span className="filter-menu-group-title">
                              Metadata filters
                          </span>
                      ),
                      children: tasksGridStore.tableMetaData.map((m) => ({
                          key: m.id,
                          label: m.title,
                          disabled: isFilterDisabled(m.id),
                          children:
                              m.fieldType === "DateTime"
                                  ? getDateMenu(m.id, true)
                                  : [
                                        {
                                            key: `Controls${m.id}`,
                                            label: (
                                                <MetadataFilterControls
                                                    filterStore={filterStore}
                                                    metadata={m}
                                                />
                                            ),
                                        },
                                    ],
                      })),
                  },
              ]
            : [];

    return (
        <Dropdown
            trigger={["click"]}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            menu={{ items: [...generalMenuItems, ...metadataMenuItems] as any }}
            overlayClassName="alpha-dropdown-overlay task-filters-menu"
        >
            <Button
                data-id="button-columns"
                className="dropdown-btn"
                style={{
                    border: "none",
                    marginRight: 10,
                    color: "#9ba0aa",
                }}
                ghost
            >
                <FilterOutlined />
                <Tooltip title="Add filters" placement="bottom">
                    Filter
                </Tooltip>
            </Button>
        </Dropdown>
    );
};

export default observer(FiltersControls);
